import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  delete_affiliation_request,
  get_affiliation_requests,
  update_affiliation_request,
} from "../../../DAL/clientTypes/ClientTpes";
import { useIds } from "../../../hook/AdminContext";
import { enqueueSnackbar } from "notistack";
import CircularLoader from "../../../components/loader/CircularLoader";
import RecordNoImage from "../../../components/recordNotFound/RecordNoImage";
import ClientsCard from "../../clientTypes/components/ClientsCard";
import CustomConfirmation from "../../../components/popUp/CustomConfirmation";
import ClientHoverImageCard from "../../clientTypes/components/ClientHoverImageCard";
import { Icon } from "@iconify/react/dist/iconify.js";
import { CircularProgress, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import GeneralMUIDialog from "../../../components/GeneralMUIDialog/GeneralMUIDialog";
import ResponseFieldFilter from "../../../components/ResponseFieldFilter/ResponseFieldFilter";
import AppliedFilters from "../../../components/AppliedFilters/AppliedFilters";
import { Funnel } from "lucide-react";
import AffiliateCard from "../../clientTypes/components/AffiliateCard";

export default function AffiliateRequest({
  // handleClentDetails = () => {},
  isManagePage = false,
}) {
  const navigate = useNavigate();
  const {
    userInfo,
    setBreadCrumb,
    fetctUserDetail,
    setUpdateReceivedAffiliatesOnNotification,
  } = useIds();
  const [requestList, setRequestList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [targetRequest, setTargetRequest] = useState(null);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  // filter states
  const [filterModal, setFilterModal] = useState(false);
  const [appliedFilterData, setAppliedFilterData] = useState(null);
  const [filterChange, setFilterChange] = useState(false);
  const [filterData, setFilterData] = useState(null);

  const handleFilterData = () => {
    setFilterModal(false);
    setIsLoading(true);
    handleGetRequestsList();
  };

  // load more states and function
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const observer = useRef();
  const lastBookElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && totalPages > pageNumber) {
          setPageNumber(pageNumber + 1);
          setIsLoadingMore(true);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, totalPages, pageNumber, observer]
  );

  const handleAgreeDelete = async () => {
    const resp = await delete_affiliation_request(targetRequest._id);
    if (resp.code == 200) {
      setRequestList((prev) =>
        prev.filter((client) => client._id !== targetRequest._id)
      );
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setDeleteConfirmation(false);
  };

  const handleRequestResponse = async (obj, value) => {
    setRequestList((prev) =>
      prev.map((client) =>
        client._id === obj._id
          ? { ...client, [`${value}loading`]: true }
          : client
      )
    );

    const postData = {
      request_id: obj._id,
      response: value,
    };

    const resp = await update_affiliation_request(postData);
    if (resp.code == 200) {
      handleGetRequestsList();
      fetctUserDetail();
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setRequestList((prev) =>
      prev.map((client) =>
        client._id === obj._id
          ? { ...client, [`${value}loading`]: false }
          : client
      )
    );
    setOpenConfirmation(false);
  };

  const handleGetRequestsList = async () => {
    const resp = await get_affiliation_requests(
      userInfo?.profile_id,
      pageNumber,
      isManagePage ? 8 : 20,
      filterData?.response?.value || ""
    );
    if (resp.code == 200) {
      if (isLoadingMore && !isManagePage) {
        let newArray = requestList.concat(resp?.data?.receiver || []);
        setRequestList(newArray);
      } else {
        setRequestList(resp.data.receiver);
      }
      setAppliedFilterData(filterData);
      setTotalPages(resp.data.total_page);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setIsLoading(false);
    setIsLoadingMore(false);
  };

  const handleViewAll = () => {
    navigate("/profile/manage-affiliate/affiliate-received");
  };

  const handleClentDetails = (item) => {
    navigate("/user/" + item?._id);
  };

  useEffect(() => {
    handleGetRequestsList();
  }, [isManagePage, pageNumber, filterChange]);

  useEffect(() => {
    if (isManagePage) return;
    setBreadCrumb([
      {
        name: `Profile`,
        path: `/profile`,
        active: false,
      },
      {
        name: `Manage Affiliate`,
        path: `/profile/manage-affiliate`,
        active: false,
      },
      {
        name: `Affiliations Sent`,
        path: `/profile/manage-affiliate/affiliate-received`,
        active: false,
      },
    ]);
  }, [isManagePage]);

  const handleUpdateAffiliateOnNotification = (
    requestData,
    setList = setRequestList
  ) => {
    if (requestData.deleted_affiliate_id) {
      setList((prev) =>
        prev.filter((item) => item._id != requestData.deleted_affiliate_id)
      );
      return;
    }
    setList((prev) => [requestData, ...prev]);
  };

  useEffect(() => {
    // Register the function
    setUpdateReceivedAffiliatesOnNotification(() => (data) => {
      handleUpdateAffiliateOnNotification(data, setRequestList);
    });
    // Cleanup: reset function on unmount
    return () => {
      setUpdateReceivedAffiliatesOnNotification(() => () => {});
    };
  }, [requestList]);

  if (isLoading) {
    return <CircularLoader />;
  }
  return (
    <>
      {openConfirmation && (
        <CustomConfirmation
          open={openConfirmation}
          setOpen={setOpenConfirmation}
          title={`Are you sure you want to reject ${targetRequest?.sender_object?.company_name}'s affiliation request?`}
          headerHeading={"Reject Affiliation"}
          handleAgree={() => {
            handleRequestResponse(targetRequest, "rejected");
          }}
        />
      )}

      {deleteConfirmation && (
        <CustomConfirmation
          openDelete={deleteConfirmation}
          setOpenDelete={setDeleteConfirmation}
          title={`Are you sure you want to delete affiliation with ${targetRequest?.sender_object?.company_name}`}
          headerHeading={"Delete Affiliation"}
          handleAgree={handleAgreeDelete}
        />
      )}

      {filterModal && (
        <GeneralMUIDialog
          open={filterModal}
          setOpen={setFilterModal}
          actionText="Filter"
          title="Filter"
          content={
            <ResponseFieldFilter
              inputs={filterData}
              setInputs={setFilterData}
              typesList={[
                {
                  value: "accepted",
                  title: "Accepted",
                },
                {
                  value: "pending",
                  title: "Pending",
                },
              ]}
            />
          }
          onSubmit={handleFilterData}
        />
      )}
      <div
        className={`container-fluid affiliate-requests ${
          isManagePage ? "mb-3" : "my-3"
        }`}
      >
        {isManagePage ? (
          <div className="d-flex justify-content-between align-items-end sub-heading-block">
            <h4>
              Affiliation Received
              {userInfo?.has_pending_affiliation_request && (
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    backgroundColor: "red",
                    borderRadius: "50%",
                    marginLeft: "1rem",
                  }}
                />
              )}
            </h4>
            <div className="d-flex align-items-center">
              <button
                className="btn-outlined-sm tw-mb-3"
                onClick={() => handleViewAll()}
              >
                View All
              </button>
            </div>
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-start heading-main tw-mb-3">
            <h3 className="mb-0">
              Affiliation Received
              {userInfo?.has_pending_affiliation_request && (
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    backgroundColor: "red",
                    borderRadius: "50%",
                    marginLeft: "1rem",
                  }}
                />
              )}
            </h3>
            <div
              className="ms-2 pointer d-flex  align-items-center justify-content-center tw-rounded-md tw-border tw-border-solid tw-border-[#0000003b]"
              style={{
                height: 38,
                width: 38,
              }}
              onClick={() => setFilterModal(true)}
            >
              <Funnel className="tw-h-4 tw-w-4" />
            </div>
          </div>
        )}

        {appliedFilterData && (
          <div>
            <AppliedFilters
              filterData={appliedFilterData}
              setFilterData={setFilterData}
              onFilterChange={() => {
                setIsLoading(true);
                setFilterChange((prev) => !prev);
              }}
            />
          </div>
        )}
        {requestList.length > 0 ? (
          <>
            <div className="d-grid gap-3 grid-template">
              {(isManagePage ? requestList.slice(0, 8) : requestList).map(
                (item) => (
                  <AffiliateCard
                    client={item.sender_object}
                    handleNavigateDetails={(item) => {
                      handleClentDetails(item);
                    }}
                    chipValue={item.response}
                    chipColor={
                      item.response == "pending"
                        ? "info"
                        : item.response == "accepted"
                        ? "success"
                        : "error"
                    }
                    showAdditionalButton={true}
                    additionButton={
                      <div className="d-flex justify-content-end pe-3 pt-2">
                        {item.response == "pending" ? (
                          <>
                            <Tooltip title="Reject Affiliation" placement="top">
                              <button
                                className="btn-white-bg-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTargetRequest(item);
                                  setOpenConfirmation(true);
                                }}
                                disabled={
                                  item[`rejectedloading`] ||
                                  item[`acceptedloading`]
                                }
                              >
                                {/* {item["rejectedloading"] ? "Rejecting...." : "Reject"} */}
                                <Icon
                                  icon={"maki:cross"}
                                  fontSize={"16px"}
                                  color="var(--portal-error-color)"
                                />
                              </button>
                            </Tooltip>
                            <Tooltip title="Accept Affiliation" placement="top">
                              <button
                                className="btn-white-bg-sm ms-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRequestResponse(item, "accepted");
                                }}
                                disabled={
                                  item[`rejectedloading`] ||
                                  item[`acceptedloading`]
                                }
                              >
                                {/* {item["acceptedloading"] ? "Accepting...." : "Accept"} */}
                                <Icon
                                  icon={"mingcute:check-fill"}
                                  fontSize={"16px"}
                                  color="var(--portal-success-color)"
                                />
                              </button>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Delete Affiliation" placement="top">
                            <button
                              className="btn-white-bg-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTargetRequest(item);
                                setDeleteConfirmation(true);
                              }}
                              disabled={item[`deleteloading`]}
                            >
                              <Icon
                                icon={"mingcute:delete-2-line"}
                                fontSize={"16px"}
                                color="var(--portal-error-color)"
                              />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    }
                  />
                )
              )}
            </div>

            {!isLoading &&
              !isManagePage &&
              totalPages > 1 &&
              totalPages > pageNumber &&
              requestList.length > 0 && (
                <div className="col-12 text-center mt-3">
                  <div
                    ref={lastBookElementRef}
                    className="mt-3"
                    onClick={() => {
                      setPageNumber(pageNumber + 1);
                    }}
                    id="load-more-feed"
                  >
                    {isLoadingMore ? (
                      <CircularProgress size={15} />
                    ) : (
                      "Load More"
                    )}
                  </div>
                </div>
              )}
          </>
        ) : (
          <div className="mt-5">
            <RecordNoImage title={"No Affiliation Received."} />
          </div>
        )}
      </div>
    </>
  );
}
